'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';
import { getCurrencyForCountry, formatLocalPrice } from '@/lib/currency';

export default function PaySection() {
  const { language, country } = useLanguage();
  const t = getTranslations(language)?.pay;
  const currency = getCurrencyForCountry(country);

  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref?.current) obs?.observe(ref?.current);
    return () => obs?.disconnect();
  }, []);

  // Local currency line items (base amounts in USD)
  const localLineItems = [
    { desc: t?.lineItems?.[0]?.desc ?? '', usd: 112.50 },
    { desc: t?.lineItems?.[1]?.desc ?? '', usd: 28.00 },
    { desc: t?.lineItems?.[2]?.desc ?? '', usd: 0 },
  ];

  const totalUSD = 140.50;

  return (
    <section id="pay" ref={ref} className="section-glow py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: copy */}
          <div className={`flex flex-col gap-8 ${visible ? 'reveal-up is-visible' : 'reveal-up'}`}>
            <div>
              <span className="chip mb-4 inline-flex">{t?.chip}</span>
              <h2 className="font-manrope font-extrabold text-dispatch-text leading-tight tracking-tight mt-4"
                style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
                {t?.headline1}<br />
                <span className="text-dispatch-cyan">{t?.headline2}</span><br />
                {t?.headline3}
              </h2>
              <p className="text-dispatch-muted text-lg font-dm-sans leading-relaxed mt-4 max-w-md">
                {t?.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {t?.chips?.map((c) => <span key={c} className="chip">{c}</span>)}
            </div>

            <div className="glass-panel rounded-xl p-5 border border-dispatch-cyan/20">
              <p className="text-dispatch-cyan font-mono font-bold text-xs uppercase tracking-widest mb-2">{t?.landlordTitle}</p>
              <p className="text-dispatch-text font-dm-sans text-sm leading-relaxed">{t?.landlordBody}</p>
            </div>

            <a href="#download" className="self-start flex items-center gap-3 px-6 py-3.5 bg-dispatch-cyan text-dispatch-bg rounded-xl font-bold font-mono text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-cyan-sm">
              {t?.cta}
            </a>
          </div>

          {/* Right: Invoice UI */}
          <div className={`${visible ? 'reveal-blur is-visible' : 'reveal-blur'}`}>
            <div className="glass-panel rounded-2xl overflow-hidden border border-dispatch-cyan/20 shadow-card">
              <div className="px-6 py-5 border-b border-dispatch-surface">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-dispatch-cyan font-mono font-bold text-sm">{t?.invoiceId}</p>
                    <p className="text-dispatch-muted font-mono text-xs mt-0.5">{t?.invoiceDate}</p>
                  </div>
                  <span className="chip bg-green-500/10 border-green-500/30 text-green-400">{t?.invoiceStatus}</span>
                </div>
              </div>

              <div className="px-6 py-4 border-b border-dispatch-surface">
                <p className="text-dispatch-faint font-mono text-[10px] uppercase tracking-widest mb-3">{t?.photoVerif}</p>
                <div className="flex gap-3">
                  {[
                    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_159f3fe43-1772209893311.png", alt: 'Close-up of leaking faucet before repair' },
                    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1cef1e2f6-1770510372536.png", alt: 'Plumber hands installing new cartridge under sink' },
                    { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf53a871-1772209895856.png", alt: 'Repaired faucet after service, no leak' },
                  ]?.map((img, i) => (
                    <div key={i} className="relative flex-1 rounded-lg overflow-hidden h-16 border border-dispatch-surface">
                      <img src={img?.src} alt={img?.alt} className="w-full h-full object-cover opacity-80" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full bg-dispatch-cyan/90 flex items-center justify-center">
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-dispatch-bg">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-6 py-4 border-b border-dispatch-surface space-y-3">
                {localLineItems?.map((item) => (
                  <div key={item?.desc} className="flex items-center justify-between">
                    <span className="text-dispatch-muted font-dm-sans text-sm">{item?.desc}</span>
                    <div className="flex items-center gap-2">
                      {item?.usd === 0 ? (
                        <span className="font-mono text-[10px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">{t?.free}</span>
                      ) : null}
                      <span className="text-dispatch-text font-manrope font-semibold text-sm">
                        {item?.usd === 0 ? formatLocalPrice(0, country) : formatLocalPrice(item?.usd, country)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-6 py-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-dispatch-muted text-sm uppercase tracking-widest">{t?.total}</span>
                  <span className="font-manrope font-extrabold text-dispatch-cyan text-2xl">
                    {formatLocalPrice(totalUSD, country)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button className="py-3 rounded-xl border border-dispatch-surface text-dispatch-muted font-mono text-xs font-semibold hover:border-dispatch-faint transition-colors">
                    {t?.dispute}
                  </button>
                  <button className="py-3 rounded-xl bg-dispatch-cyan text-dispatch-bg font-mono text-xs font-bold hover:bg-white transition-colors shadow-cyan-sm">
                    {t?.approve}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}